import { Icon, MultiSelect } from '@distribusion/owl-frontend-ui'
import { useFormikContext } from 'formik'
import React, { ReactElement, useState, useMemo } from 'react'

import { SearchFormState } from '@components/SearchForm'
import StopoverModal from '@components/SearchForm/StopOver/ModalField/Modal'
import { useTranslation } from '@lib/i18n'
import { ViaRule } from 'src/stores/params'

import '@components/SearchForm/StopOver/ModalField/ModalButton.scss'

interface StopoverModalButtonProps {
  stopoverData?: Location.NamedItem[]
  viasRulesData?: ViaRule
}

const StopoverModalButton = ({ stopoverData = [], viasRulesData = {} }: StopoverModalButtonProps): ReactElement => {
  const { t } = useTranslation()
  const { setFieldValue } = useFormikContext<SearchFormState>()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleOpenModal = (): void => {
    setIsModalOpen(true)
  }

  const handleCloseModal = (): void => {
    setIsModalOpen(false)
  }

  const handleRemove = (valueToRemove?: string): void => {
    const updatedData = stopoverData.filter(({ code }) => code !== valueToRemove)
    setFieldValue('stopOverStations', updatedData)
  }

  const mappedLocations = useMemo(
    () => stopoverData.map(item => ({ label: item.name, value: item.code })),
    [stopoverData],
  )

  // Show only first chip and "+N" indicator for additional items
  const displayedLocations = useMemo(() => {
    if (mappedLocations.length <= 1) {
      return mappedLocations
    }
    return [mappedLocations[0]]
  }, [mappedLocations])

  const additionalCount = mappedLocations.length > 1 ? mappedLocations.length - 1 : 0

  const firstLocationCode = displayedLocations[0]?.value
  const firstLocationRule = firstLocationCode ? viasRulesData[firstLocationCode] : undefined
  const iconName =
    firstLocationRule === 'include' || firstLocationRule === undefined ? 'checkout-circle' : 'cross-circle'

  return (
    <>
      <div className="stopover-modal-button" onClick={handleOpenModal}>
        <div className="stopover-modal-button__content">
          <div className="stopover-modal-button__icon">
            <Icon name="stop-over" size="large" />
          </div>
          <div className="stopover-modal-button__text-section">
            <div className="stopover-modal-button__label">{t('searchBar.stopOver.label')}</div>
            <div className="stopover-modal-button__multiselect-container">
              <MultiSelect
                values={displayedLocations}
                inputValue=""
                onRemove={handleRemove}
                onInputChange={() => {}} // No-op since we handle input in modal
                placeholder={mappedLocations.length === 0 ? t('searchBar.stopOver.placeholder') : undefined}
                disabled={false}
                iconName={iconName}
              />
              {additionalCount > 0 && <div className="stopover-modal-button__additional-count">+{additionalCount}</div>}
            </div>
          </div>
        </div>
      </div>

      <StopoverModal opened={isModalOpen} onClose={handleCloseModal} />
    </>
  )
}

export default StopoverModalButton
